// ── DAG node/edge data types for the redesigned Pipeline tab ─────────────────
//
// These types decouple the new per-service DAG (one node per PipelineStep row)
// from the legacy group-node PipelineNodeData used by the old PipelineDAGView.
// See contracts/pipeline-visualization.yaml (dag_node, dag_edge, node_states).

/** Canonical status values that a DAG node can display. */
export type DagStatus =
  "success" | "failed" | "running" | "pending" | "skipped";

/** Service type label badge (from rusha.yml). */
export type ServiceType =
  "server" | "static" | "worker" | "stateful" | "cron" | "scheduler";

/** Phase (column) keys derived from PipelineStep.step values. */
export type DagPhase =
  | "build"
  | "security_scan"
  | "quality_gate"
  | "deploy"
  | "verify"
  | "vcs_provisioning";

/** Data carried by each DAG node (one per PipelineStep row). */
export interface DagNodeData {
  phase: DagPhase;
  /** Service name, or empty string for phase-level steps (e.g. verify). */
  service: string;
  status: DagStatus;
  /** Service type label badge, or empty string for phase-level nodes. */
  type: string;
  /** Human-readable duration string (e.g. "1m 12s"), or null. */
  duration: string | null;
  /** Display label — serviceName or phase name for phase-level nodes. */
  label: string;
  /** Failure message for failed nodes (from PipelineStep.message). */
  message: string;
  executions: PipelineStepExecution[];
  [key: string]: unknown;
}

export interface PipelineStepExecution {
  id: number;
  name: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  logRef: string | null;
}

/** Maps internal step status strings to canonical DagStatus. */
export function toDagStatus(status: string): DagStatus {
  switch (status) {
    case "completed":
    case "success":
      return "success";
    case "failed":
      return "failed";
    case "in_progress":
    case "running":
      return "running";
    case "skipped":
      return "skipped";
    case "pending":
    case "queued":
    default:
      return "pending";
  }
}

/** Ordered phase precedence for column ordering (left → right). */
export const PHASE_ORDER: DagPhase[] = [
  "vcs_provisioning",
  "build",
  "security_scan",
  "quality_gate",
  "deploy",
  "verify",
];

/**
 * Canonical phases per deploy mode (from pipeline-visualization.yaml).
 * Used to build a consistent DAG structure across all runs of the same stack.
 * The DAG shape is fixed per deploy mode — only node statuses/durations
 * change per run.
 */
export const PIPELINE_MODE_PHASES: Record<string, DagPhase[]> = {
  rusha_managed: ["quality_gate", "build", "security_scan", "deploy", "verify"],
  ci_build_rusha_deploy: ["quality_gate", "deploy", "verify"],
};

/** Human-readable phase column headers. */
export const PHASE_LABELS: Record<DagPhase, string> = {
  vcs_provisioning: "Setup",
  build: "Build",
  security_scan: "Scan",
  quality_gate: "Quality gate",
  deploy: "Deploy",
  verify: "Verify",
};

/** Deploy-mode → human-readable label for run list + run header chips. */
export const PIPELINE_MODE_LABELS: Record<string, string> = {
  rusha_managed: "Rusha managed",
  ci_build_rusha_deploy: "CI build · Rusha deploy",
};

/** Short deploy-mode label for the run list sub-line. */
export const PIPELINE_MODE_SHORT: Record<string, string> = {
  rusha_managed: "Rusha",
  ci_build_rusha_deploy: "CI build",
};

/** Status dot + left-border accent color per DagStatus. */
export const STATUS_COLORS: Record<DagStatus, string> = {
  success: "#10b981",
  failed: "#ef4444",
  running: "#3b82f6",
  pending: "#94a3b8",
  skipped: "#94a3b8",
};
